import { StateOfChargeDeratingHelpers } from '@diagram/helpers/StateOfChargeDeratingHelpers';

import { ComponentInstanceValidationErrorType, ComponentInstanceValidator } from 'models';

import { busGrounding } from './component-instance-bus-grounding';
import { coreSpecifications } from './component-instance-core-specifications';
import { dcPortLines } from './component-instance-dc-port-lines';
import { powerVoltageDroop } from './component-instance-power-voltage-droop';
import { stateOfChargeDerating } from './component-instance-state-of-charge-derating';
import { voltageConfiguration } from './component-instance-voltage-configuration';
import { converterControlMethods } from './converter-control-methods';
import { protectionDirectionality } from './protection-directionality';

export const componentInstanceValidator: ComponentInstanceValidator = {
    type: 'componentInstance',
    validator: (props) => {
        return [
            ...coreSpecifications(props),
            ...converterControlMethods(props),
            ...voltageConfiguration(props),
            ...powerVoltageDroop(props),
            ...stateOfChargeDerating(props),
            ...protectionDirectionality(props),
            ...busGrounding(props),
            ...dcPortLines(props),
        ];
    },
    formatters: {
        [ComponentInstanceValidationErrorType.CONTROL_METHODS_MISSING]: (error) => ({
            title: `${error.payload.componentInstance.designator} has insufficient control methods`,
            description: 'Specify additional control methods in the simulation tab.',
            explain: `
### What this means
This component does not define **how it behaves electrically**.
At least one port must specify how voltage or current is controlled.

### Why this is a problem
- The component’s electrical role is unclear
- Simulations cannot determine how it interacts with the rest of the zone
- Other components may depend on this behavior

### How to fix it
- Define at least one control method
- Typical control methods include:
  - Constant voltage
  - Voltage droop
  - Current or power control
            `,
            features: {
                simulations: true,
                wireSizing: false,
            },
        }),

        [ComponentInstanceValidationErrorType.CONTROL_METHODS_TOO_MANY]: (error) => ({
            title: `${error.payload.componentInstance.designator} has too many control methods specified`,
            description:
                'Leave one port uncontrolled in the simulation tab, or pair Maximum Power Point Tracking with Power-Voltage Droop Control.',
            explain: `
### What this means
This component attempts to control **too many electrical variables at once**.

### Why this is a problem
- A component cannot fully control all ports simultaneously
- Over-constraining the system makes it unsolvable
- Electrical behavior becomes contradictory

### How to fix it
Pick whichever of these matches what the component does:

- Leave one port **uncontrolled** and use it as a passive connection. Note that the zone on the
  uncontrolled side then needs something else to set its voltage, which a solar panel only does
  when its own control method is **Power-Voltage Droop Control**.
- On a two-port converter tracking a solar array, set the array side to **Maximum Power Point
  Tracking** and the other side to **Power-Voltage Droop Control**. This pair is supported and
  needs no uncontrolled port. The solar panel it feeds has to be on **Constant Current**.
            `,
            features: {
                simulations: true,
                wireSizing: false,
            },
        }),

        [ComponentInstanceValidationErrorType.CORE_SPECIFICATIONS_CURRENT]: (error) => {
            const { componentInstance, portIndex } = error.payload;

            return {
                title: `${componentInstance.designator} has no current specified`,
                description: `This component needs a nominal or maximum current at port ${portIndex! + 1}.`,
                explain: `
### What this means
The maximum or nominal **current rating** of this port is missing.

### Why this is a problem
- Current determines:
  - Thermal loading
  - Cable sizing
  - Protection limits
- Without it, safety checks cannot be performed

### How to fix it
- Specify a nominal or maximum current for this port
- Use manufacturer or design values
                `,
                features: {
                    simulations: false,
                    wireSizing: true,
                },
            };
        },

        [ComponentInstanceValidationErrorType.CORE_SPECIFICATIONS_POWER]: (error) => {
            const { componentInstance, portIndex } = error.payload;

            return {
                title: `${componentInstance.designator} has no power specified`,
                description: `This component needs a nominal or maximum power at port ${portIndex! + 1}.`,
                explain: `
### What this means
The **power rating** of this port is not defined.

### Why this is a problem
- Power defines how much energy the component consumes or delivers
- It is required for:
  - Load balancing
  - Thermal limits
  - Protection coordination

### How to fix it
- Specify the nominal or maximum power for this port
                `,
                features: {
                    simulations: false,
                    wireSizing: true,
                },
            };
        },

        [ComponentInstanceValidationErrorType.CORE_SPECIFICATIONS_VOLTAGE]: (error) => {
            const { componentInstance, portIndex } = error.payload;

            return {
                title: `${componentInstance.designator} has no voltage specified`,
                description: `This component needs a nominal voltage at port ${portIndex! + 1}.`,
                explain: `
### What this means
The expected **operating voltage** of this port is missing.

### Why this is a problem
- Components are designed for specific voltage ranges
- Incorrect voltage assumptions may:
  - Damage components
  - Produce invalid simulation results

### How to fix it
- Define the nominal operating voltage for this port
                `,
                features: {
                    simulations: false,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.PROTECTION_DIRECTION_BIDIRECTIONAL_ON_UNIDIRECTIONAL_FLOW]: (error) => {
            const { componentInstance } = error.payload;

            return {
                title: `${componentInstance.designator} declares bidirectional protection on a unidirectional circuit`,
                description: `${componentInstance.designator} is bPuF: its protection is declared for flow that can reverse, on a circuit declared to flow one way only.`,
                explain: `
### What this means
Current/OS labels a protection device \`wPzF\`, where \`w\` is the direction it can **protect** in and \`z\` is the direction power can **flow** in.
${componentInstance.designator} is **bPuF**: bidirectional protection, unidirectional flow.

### Why this is a problem
- Bidirectional protection presupposes a circuit whose flow can reverse
- Declaring it where the flow is one way describes a device that cannot exist as specified
- The two halves of the designation contradict each other, so one of them is wrong and will carry that error into anything that reads it

Note that the opposite pairing, **unidirectional protection on bidirectional flow** (uPbF), is a valid configuration and is not flagged.

### How to fix it
- Set the port power flow to **Bidirectional** if this circuit really can be fed from either end, or
- Set the protection direction to **Unidirectional** to match the one-way flow already declared on the ports
                `,
                features: {
                    simulations: false,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_BATTERY_VOLTAGE]: (error) => {
            const { componentInstance } = error.payload;

            return {
                title: `Battery ${componentInstance.designator} has no voltage specified`,
                description: `A battery needs a voltage to function within a zone.`,
                explain: `
### What this means
The battery does not specify its **output voltage**.

### Why this is a problem
- Batteries define the voltage of the zone they feed
- Without a voltage, connected loads cannot operate correctly

### How to fix it
- Specify the battery’s nominal voltage
                `,
                features: {
                    simulations: false,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_BUS_GROUNDING_IMPEDANCE]: (error) => {
            const { componentInstance } = error.payload;

            return {
                title: `${componentInstance.designator} has no grounding impedance specified`,
                description: 'A symmetrical impedance grounded bus needs an impedance on both L+ and L-.',
                explain: `
### What this means
${componentInstance.designator} references both DC poles to ground, each through its own impedance. One or both of
those impedances is missing or zero.

### Why this is a problem
- The two impedances are the only thing setting the pole-to-ground potentials, so without them the bus has no
  defined reference and the poles float
- They are also the only thing bounding ground-fault current. A zero impedance is a solidly grounded pole, which is
  a different grounding arrangement
- Fault results would be produced against a circuit you did not specify

### How to fix it
- Click ${componentInstance.designator} on the diagram
- Open the grounding section in the sidebar
- Set an **L+ grounding impedance** and an **L- grounding impedance** greater than zero
                `,
                features: {
                    simulations: true,
                    fault: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_BUS_GROUNDING_SYMMETRY]: (error) => {
            const { componentInstance } = error.payload;

            return {
                title: `${componentInstance.designator} has unequal grounding impedances`,
                description: 'A symmetrical impedance grounded bus needs the same impedance on L+ and L-.',
                explain: `
### What this means
${componentInstance.designator} is set to symmetrical impedance grounding, but its L+ and L- grounding impedances
differ.

### Why this is a problem
- Equal impedances are what put the virtual midpoint at ground potential. Unequal ones shift it, so the two poles
  no longer sit at plus and minus half the pole-to-pole voltage
- The arrangement would no longer be the one the configuration names, and the results would not describe the system
  you drew

### How to fix it
- Click ${componentInstance.designator} on the diagram
- Open the grounding section in the sidebar
- Set the same value for **L+ grounding impedance** and **L- grounding impedance**
                `,
                features: {
                    simulations: true,
                    fault: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_DC_PORT_LINES]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;

            return {
                title: `${componentInstance.designator} has L+, M and L- connected on ${portLabel}`,
                description: `${componentInstance.designator} has two DC terminals. Connect only two of L+, M and L- on ${portLabel}.`,
                explain: `
### What this means
The connection to ${componentInstance.designator} on ${portLabel} carries **L+, M and L-**, but
${componentInstance.designator} only has two DC terminals.

### Why this is a problem
- One of the three conductors has nowhere to go inside the component
- The simulator cannot balance the equations for that conductor, so the power flow fails to start

### How to fix it
Open the connection to ${componentInstance.designator} and remove one of the lines:
- Remove **M** and keep L+ and L- to supply the full pole-to-pole voltage
- Remove **L-** and keep L+ and M to supply half of it
- Remove **L+** and keep M and L- to supply half of it
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_CONVERTER_VOLTAGE]: () => ({
            title: 'Converter has no voltage specified',
            description: 'A converter needs a port with constant voltage as control method.',
            explain: `
### What this means
The converter is missing a **voltage-defining control method**.

### Why this is a problem
- A converter must clearly define what voltage it produces
- Without this, it cannot regulate the zone correctly

### How to fix it
- Assign a constant-voltage control method to one port
            `,
            features: {
                simulations: true,
                wireSizing: false,
            },
        }),

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_CONVERTER_DROOP_RESISTANCE]: () => ({
            title: 'Converter has no droop resistance specified',
            description: 'A converter needs a port with constant voltage as control method.',
            explain: `
### What this means
The converter uses **Voltage Droop Control** but lacks a droop resistance value.

### Why this is a problem
- Droop resistance defines how voltage decreases with load
- Without it, voltage-current behavior is undefined

### How to fix it
- Specify a droop resistance value
- Use design or manufacturer guidance
            `,
            features: {
                simulations: true,
                wireSizing: false,
            },
        }),

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_GENERATOR_VOLTAGE]: (error) => {
            const { componentInstance } = error.payload;

            return {
                title: `Generator ${componentInstance.designator} has no voltage specified`,
                description: `A generator needs a voltage to function within a zone.`,
                explain: `
### What this means
The generator does not specify the voltage it produces.

### Why this is a problem
- Generators usually set the voltage level of a zone
- Loads depend on this value to operate correctly

### How to fix it
- Define the generator’s nominal output voltage
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_UTILITY_VOLTAGE]: (error) => {
            const { componentInstance } = error.payload;

            return {
                title: `Utility ${componentInstance.designator} has no voltage specified`,
                description: `A utility needs a voltage to function within a zone.`,
                explain: `
### What this means
The utility connection does not specify its **supply voltage**.

### Why this is a problem
- Utilities act as voltage references for the entire zone
- All downstream components rely on this value

### How to fix it
- Specify the nominal utility voltage
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_POWER_VOLTAGE_DROOP]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;

            return {
                title: `${componentInstance.designator} has no power-voltage droop points defined`,
                description: `Add at least one point to the Power-voltage droop chart on ${portLabel} in the Simulation tab of ${componentInstance.designator}.`,
                explain: `
### What this means
${componentInstance.designator} is configured with **Power-Voltage Droop Control** on ${portLabel}, but its **droop curve has no points**.

### Why this is a problem
- Droop control needs at least one (voltage, power) point to define how power varies with voltage
- Without any points, the simulator has no curve to follow and the run will fail

### How to fix it
- Click ${componentInstance.designator} on the diagram
- Open the **Simulation** tab in the sidebar
- On ${portLabel}, add at least one point to the **Power-voltage droop** chart
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_COMPONENT]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;

            return {
                title: `${componentInstance.designator} cannot derate by state of charge`,
                description: `Only a converter's port can follow a battery's state of charge. Switch off "Derate by state of charge" on ${portLabel} in the Simulation tab of ${componentInstance.designator}.`,
                explain: `
### What this means
"Derate by state of charge" is on for ${portLabel} of ${componentInstance.designator}, which is not a converter.

### Why this is a problem
- Derating follows the battery behind a converter, so only a converter between a battery and the bus can do it
- The simulator refuses to run with it on any other component

### How to fix it
- Switch off **Derate by state of charge** on ${portLabel} in the Simulation tab of ${componentInstance.designator}
- Switch it on at the converter between the battery and the bus instead
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_MPPT]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;
            const trackingPort = StateOfChargeDeratingHelpers.getMaximumPowerPointTrackingPort(componentInstance);
            const trackingPortLabel = trackingPort === null ? 'its other port' : `port ${trackingPort + 1}`;

            return {
                title: `${componentInstance.designator} combines state of charge derating with Maximum Power Point Tracking`,
                description: `"Derate by state of charge" cannot be combined with Maximum Power Point Tracking on ${trackingPortLabel}. Switch it off on ${portLabel} in the Simulation tab of ${componentInstance.designator}.`,
                explain: `
### What this means
${componentInstance.designator} tracks a solar array's maximum power point on ${trackingPortLabel} and has
"Derate by state of charge" on for ${portLabel}.

### Why this is a problem
- A converter tracking a solar array follows the array, not a battery
- The simulator refuses to run with both on one converter

### How to fix it
- Switch off **Derate by state of charge** on ${portLabel} in the Simulation tab of ${componentInstance.designator}
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.REQUIRED_CONFIGURATION_STATE_OF_CHARGE_DERATING_BATTERY]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;

            return {
                title: `${componentInstance.designator} has no battery to derate by`,
                description: `"Derate by state of charge" needs one battery to follow, but no battery is connected to the other ports of ${componentInstance.designator}. Choose the battery under "Battery" on ${portLabel}.`,
                explain: `
### What this means
"Derate by state of charge" is on for ${portLabel} of ${componentInstance.designator}, but no battery sits at the
end of its other ports, through breakers, fuses and cables.

### Why this is a problem
- The derating follows one battery's state of charge
- Without one, the simulator has nothing to follow and refuses to run

### How to fix it
- Choose the battery under **Battery** on ${portLabel} in the Simulation tab of ${componentInstance.designator}, or
- Connect the battery directly behind ${componentInstance.designator}
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_BATTERIES]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;

            return {
                title: `${componentInstance.designator} has several batteries to derate by`,
                description: `"Derate by state of charge" needs one battery to follow, but several batteries are connected to the other ports of ${componentInstance.designator}. Choose the battery under "Battery" on ${portLabel}.`,
                explain: `
### What this means
"Derate by state of charge" is on for ${portLabel} of ${componentInstance.designator}, and more than one battery
sits at the end of its other ports.

### Why this is a problem
- The derating follows one battery's state of charge
- The simulator cannot tell which one to follow and refuses to run

### How to fix it
- Choose the battery under **Battery** on ${portLabel} in the Simulation tab of ${componentInstance.designator}
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },

        [ComponentInstanceValidationErrorType.INVALID_CONFIGURATION_STATE_OF_CHARGE_DERATING_SOURCE]: (error) => {
            const { componentInstance, portIndex } = error.payload;
            const portLabel = `port ${(portIndex ?? 0) + 1}`;

            return {
                title: `${componentInstance.designator} derates by a component that is not a battery`,
                description: `The component chosen under "Battery" on ${portLabel} of ${componentInstance.designator} is not a battery or no longer exists. Choose a battery.`,
                explain: `
### What this means
"Derate by state of charge" on ${portLabel} of ${componentInstance.designator} follows the component chosen under
"Battery", but that component is not a battery or has been removed from the diagram.

### Why this is a problem
- Only a battery has a state of charge to follow
- The simulator refuses to run with anything else

### How to fix it
- Choose a battery under **Battery** on ${portLabel} in the Simulation tab of ${componentInstance.designator}
                `,
                features: {
                    simulations: true,
                    wireSizing: false,
                },
            };
        },
    },
};
